import React, { useState, useEffect, useCallback, useMemo } from "react";
import api from "../lib/api";
import { useNavigate } from "react-router-dom";
import "./SalaryAdmin.css";

// 期間(YYYYMM)からラベル生成
const periodToLabel = (period) => {
  if (!period || period.length < 6) return period;
  return `${period.slice(0, 4)}年${period.slice(4, 6)}月`;
};

const SalaryZipUpload = () => {
  const [files, setFiles] = useState([]);
  const [uploadResult, setUploadResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [departments, setDepartments] = useState([]);
  // カレンダー用 YYYY-MM 形式の初期値
  const [checkMonth, setCheckMonth] = useState(() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    return `${y}-${m}`;
  });
  // YYYY-MM → YYYYMM に変換（全角→半角正規化も含む）
  const checkPeriod = checkMonth.replace("-", "").replace(/[０-９]/g, (c) =>
    String.fromCharCode(c.charCodeAt(0) - 0xFEE0)
  );
  const [searchName, setSearchName] = useState("");
  const [selectedDeptId, setSelectedDeptId] = useState("");
  const [selectedKaName, setSelectedKaName] = useState("");
  const [missingRows, setMissingRows] = useState([]);
  const [missingLoading, setMissingLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/departments/").then((r) => setDepartments(r.data || [])).catch(() => setDepartments([]));
  }, []);

  const selectedDept = useMemo(
    () => (Array.isArray(departments) ? departments.find((d) => d.departmentId === selectedDeptId) : null),
    [departments, selectedDeptId]
  );
  {/*const effectiveDepartmentName = selectedKaName || (selectedDept?.departmentName ?? "") || "";*/}
  const searchDepartmentName = selectedKaName || (selectedDept?.departmentName ?? "") || "";

  const deptOptions = useMemo(() => {
    return (departments || [])
      .filter((d) => d.departmentId && d.departmentId.endsWith("00") && !d.departmentId.startsWith("00"))
      .sort((a, b) => (a.departmentName || "").localeCompare(b.departmentName || ""));
  }, [departments]);

  const kaOptions = useMemo(() => {
    return (selectedDept?.children || [])
      .filter((c) => c.departmentName)
      .sort((a, b) => (a.departmentName || "").localeCompare(b.departmentName || ""));
  }, [selectedDept]);

  const fetchMissing = useCallback(async () => {
    setMissingLoading(true);
    try {
      const res = await api.get("/salary/admin/missing/", {
        params: {
          period: checkPeriod,
          ...(searchName ? { name: searchName } : {}),
          ...(searchDepartmentName ? { departmentName: searchDepartmentName } : {}),
        },
      });
      setMissingRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("未アップロード一覧の取得に失敗しました。", err);
      setMissingRows([]);
    } finally {
      setMissingLoading(false);
    }
  }, [checkPeriod, searchName, searchDepartmentName]);

  useEffect(() => {
    fetchMissing();
  }, [fetchMissing]);

  const handleFiles = (e) => {
    setFiles(Array.from(e.target.files));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!files.length) {
      alert("PDFまたはZIPファイルを選択してください。");
      return;
    }
    if (!window.confirm("給与明細をアップロードしてもよろしいですか？")) return;

    const fd = new FormData();
    files.forEach((f) => fd.append("file", f));

    setBusy(true);
    setUploadResult(null);
    try {
      const res = await api.post("/upload-zip/", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadResult(res.data);
      setFiles([]);
      // ファイル選択をリセット
      const input = document.getElementById("salary-file-input");
      if (input) input.value = "";

      // アップロードしたファイルの対象期間を検出して自動切替
      // バックエンドのレスポンスに含まれる periods（例: ["202603"]）を優先し、
      // なければフロントのファイル名から解析する
      const responsePeriods = Array.isArray(res.data?.periods) ? res.data.periods : [];
      const detectedPeriod = responsePeriods.length > 0
        ? responsePeriods[0]
        : (() => {
            for (const f of files) {
              const m = /[A-Za-z0-9]+_(\d{6})\.pdf$/i.exec(f.name);
              if (m) return m[1];
            }
            return null;
          })();

      if (detectedPeriod && detectedPeriod.length === 6) {
        const newCheckMonth = `${detectedPeriod.slice(0, 4)}-${detectedPeriod.slice(4, 6)}`;
        if (newCheckMonth !== checkMonth) {
          // checkMonth を変更すると useEffect 経由で fetchMissing が自動実行される
          setCheckMonth(newCheckMonth);
        } else {
          await fetchMissing();
        }
      } else {
        await fetchMissing();
      }
    } catch (err) {
      alert(
        "アップロードエラーが発生しました。" +
          (err.response?.data?.error || err.message)
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="salary-admin-page">
      <div className="salary-admin-header">
        <h2 className="salary-admin-title">給与管理</h2>
        <button
          type="button"
          className="salary-back-btn"
          onClick={() => navigate("/dashboard")}
        >
          メニュー画面に戻る
        </button>
      </div>

      {/* アップロードフォーム */}
      <section className="salary-upload-section">
        <h3 className="salary-section-title">給与明細アップロード</h3>
        <form onSubmit={handleSubmit} className="salary-upload-form">
          <div className="salary-file-field">
            <label htmlFor="salary-file-input" className="salary-file-label">
              ファイルを選択 (ZIP または PDF)
            </label>
            <input
              id="salary-file-input"
              type="file"
              onChange={handleFiles}
              multiple
              accept=".zip,application/pdf"
              className="salary-file-input"
            />
            <p className="salary-file-hint">
              ファイル名の形式: <code>会社名_年月（または令和　年　月）コード_社員名.pdf</code>（例: ○○株式会社_令和8年3月_S001_山田太郎.pdf）
            </p>
          </div>

          <button type="submit" disabled={busy} className="salary-upload-btn">
            {busy ? "アップロード中..." : "アップロード"}
          </button>
        </form>

        {/* アップロード結果 */}
        {uploadResult && (
          <div className="salary-upload-result">
            <p className="salary-result-saved">
              ✅ アップロード完了しました。（{uploadResult.saved}件保存）
            </p>
            {uploadResult.skipped && uploadResult.skipped.length > 0 && (
              <div className="salary-result-skipped">
                <p className="salary-skipped-title">
                  スキップされたファイル ({uploadResult.skipped.length}件):
                </p>
                <ul className="salary-skipped-list">
                  {uploadResult.skipped.map((s, i) => (
                    <li key={i}>
                      {s.file} —{" "}
                      {s.reason === "invalid_name"
                        ? "ファイル名の形式が正しくありません。"
                        : s.reason === "no_employee"
                        ? "対応する社員が見つかりません。"
                        : s.reason === "no_company_info"
                        ? "会社情報（会社名）が未登録です。"
                        : s.reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </section>

      {/* アップロード確認（未アップロード社員の検索） */}
      <section className="salary-list-section">
        <h3 className="salary-section-title">アップロード確認（未アップロード社員）</h3>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
          <label style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
            対象月:
            <input
              type="month"
              value={checkMonth}
              onChange={(e) => setCheckMonth(e.target.value)}
              style={{ padding: 6 }}
            />
          </label>
          <label style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
            名前:
            <input
              type="text"
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              placeholder="部分一致"
              style={{ padding: 6, width: 160 }}
            />
          </label>
          <label style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
            部署:
            <select
              value={selectedDeptId}
              onChange={(e) => { setSelectedDeptId(e.target.value); setSelectedKaName(""); }}
              style={{ padding: 6, minWidth: 160 }}
            >
              <option value="">全ての部署</option>
              {deptOptions.map((d) => (
                <option key={d.departmentId} value={d.departmentId}>{d.departmentName}</option>
              ))}
            </select>
          </label>
          {selectedDeptId && (
            <label style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
              課:
              <select value={selectedKaName} onChange={(e) => setSelectedKaName(e.target.value)} style={{ padding: 6, minWidth: 160 }}>
                <option value="">課を選択</option>
                {kaOptions.map((k) => (
                  <option key={k.departmentId || k.departmentName} value={k.departmentName}>{k.departmentName}</option>
                ))}
              </select>
            </label>
          )}
          <button type="button" className="salary-upload-btn" onClick={fetchMissing} disabled={missingLoading}>
            {missingLoading ? "検索中..." : "検索"}
          </button>
        </div>

        {missingLoading ? (
          <p className="salary-list-loading">読み込み中...</p>
        ) : missingRows.length === 0 ? (
          <p className="salary-list-empty">未アップロードの社員はありません。</p>
        ) : (
          <div className="salary-list-wrap">
            <table className="salary-list-table">
              <thead>
                <tr>
                  <th>社員コード</th>
                  <th>氏名</th>
                  <th>部署</th>
                  <th>課</th>
                  <th>対象月</th>
                </tr>
              </thead>
              <tbody>
                {missingRows.map((r) => (
                  <tr key={r.memberId}>
                    <td>{r.memberId}</td>
                    <td>{r.name}</td>
                    <td>{(r.departmentBuName != null && String(r.departmentBuName).trim() !== "") ? r.departmentBuName : "—"}</td>
                    <td>{(r.departmentKaName != null && String(r.departmentKaName).trim() !== "") ? r.departmentKaName : "—"}</td>
                    <td>{r.period ? periodToLabel(r.period) : periodToLabel(checkPeriod)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default SalaryZipUpload;
